// SelectionBoxLarge.jsx
import React from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
    base: {
        position: 'relative',
        borderRadius: tokens.borderRadiusLarge,
        cursor: 'pointer',
        transitionProperty: 'border-color, box-shadow, background-color',
        transitionDuration: tokens.durationNormal,
        boxSizing: 'border-box',
        backgroundColor: '#fff',
        border: '1.5px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingTop: '14px',
        paddingBottom: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
        width: '100%',
        height: '100%',
        '@media (max-width: 640px)': {
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '10px',
            paddingRight: '10px',
        },
    },
    hoverable: {
        ':hover': {
            borderColor: '#09aa89',
            boxShadow: '0 2px 10px rgba(9, 170, 137, 0.10)',
        },
    },
    active: {
        border: '2px solid #09aa89',
        backgroundColor: '#f0faf8',
        boxShadow: '0 2px 12px rgba(9, 170, 137, 0.14)',
    },
    disabled: {
        cursor: 'not-allowed',
        opacity: 0.55,
        backgroundColor: '#f9fafb',
        pointerEvents: 'none',
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '8px',
        '@media (max-width: 640px)': {
            gap: '6px',
            marginBottom: '5px',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
        },
    },
    icon: {
        width: '38px',
        height: '38px',
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: '#f0faf8',
        color: '#09aa89',
        fontSize: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transitionProperty: 'background-color, color',
        transitionDuration: tokens.durationFast,
        '@media (max-width: 640px)': {
            width: '30px',
            height: '30px',
            fontSize: '16px',
        },
    },
    iconActive: {
        backgroundColor: '#09aa89',
        color: '#fff',
    },
    title: {
        fontSize: '13.5px',
        fontWeight: tokens.fontWeightSemibold,
        color: '#111827',
        lineHeight: '1.25',
        '@media (max-width: 640px)': {
            fontSize: '11px',
            textAlign: 'center',
        },
    },
    titleActive: {
        color: '#09aa89',
    },
    subtitle: {
        fontSize: '11.5px',
        color: '#6b7280',
        lineHeight: '1.45',
        marginBottom: '10px',
        flexGrow: 1,
        '@media (max-width: 640px)': {
            display: 'none',
        },
    },
    listFooter: {
        paddingTop: '10px',
        borderTop: '1px solid #f0f0f0',
        width: '100%',
        '@media (max-width: 640px)': {
            display: 'none',
        },
        // Recreates the legacy .check-list/.error-icon bullet look locally (instead of
        // relying on SelectionBoxLarge.scss) so callers that still pass raw
        // <ul className="check-list"><li className="error-icon"> markup via `footer`
        // keep rendering unchanged.
        '& .check-list': {
            listStyleType: 'none',
            paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
        },
        '& .check-list li': {
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            color: '#374151',
            lineHeight: '1.3',
        },
        '& .check-list li::before': {
            content: '"✓"',
            color: '#09aa89',
            fontWeight: tokens.fontWeightBold,
            marginRight: '6px',
            fontSize: '11px',
            flexShrink: 0,
            lineHeight: '1',
        },
        '& .check-list li.error-icon': {
            color: '#9ca3af',
            textDecorationLine: 'line-through',
        },
        '& .check-list li.error-icon::before': {
            content: '"✗"',
            color: '#f87171',
        },
    },
});

const SelectionBoxLarge = ({
    title,
    subTitle,
    selected,
    onClick,
    footer,
    disabled = false,
    icon = null
}) => {
    const s = useStyles();

    const handleClick = (e) => {
        if (!disabled && onClick) {
            onClick(e);
        }
    }

    return (
        <div
            onClick={handleClick}
            className={mergeClasses(
                s.base,
                !disabled && s.hoverable,
                selected && s.active,
                disabled && s.disabled,
            )}
        >
            <div className={s.header}>
                <div className={mergeClasses(s.icon, selected && s.iconActive)}>{icon}</div>
                <div className={mergeClasses(s.title, selected && s.titleActive)}>{title}</div>
            </div>

            {subTitle && <div className={s.subtitle}>{subTitle}</div>}

            {footer && (
                <div className={s.listFooter}>
                    {footer}
                </div>
            )}
        </div>
    );
};

export default SelectionBoxLarge;
